/**
 * InvoicesHub — Page Factures unifiée avec tabs internes.
 *
 * Objectif UX : un seul endroit pour tout ce qui touche aux factures émises.
 * Fini les 4 rubriques sidebar séparées ("Factures émises", "Récurrentes",
 * "Import en masse", "Historique imports") — on regroupe dans 3 onglets clairs :
 *
 *   ┌─ Toutes         (liste + actions unitaires)
 *   ├─ Récurrentes    (abonnements, avec badge count)
 *   └─ Import & OCR   (batch PDF + historique des jobs)
 *
 * L'onglet actif est piloté par le query param `?tab=` pour permettre
 * les deep-links depuis n'importe où (ex: /app/invoices?tab=import).
 *
 * Compat descendante : les routes /app/invoices/recurring, /import-batch,
 * /import-jobs restent fonctionnelles via des redirects (App.js).
 */
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileText, Repeat, Upload } from "lucide-react";
import InvoicesList from "@/pages/InvoicesList";
import RecurringInvoices from "@/pages/RecurringInvoices";
import ImportVentesBatch from "@/pages/ImportVentesBatch";
import ImportJobs from "@/pages/ImportJobs";

const TAB_ALIASES = {
  all: "all", toutes: "all",
  recurring: "recurring", recurrentes: "recurring",
  import: "import", imports: "import", ocr: "import",
};

export default function InvoicesHub() {
  const [params, setParams] = useSearchParams();
  const activeParam = params.get("tab");
  const active = TAB_ALIASES[activeParam] || "all";
  const [recurringCount, setRecurringCount] = useState(null);
  const [runningJobs, setRunningJobs] = useState(0);

  // Compteurs pour badges — chargés une seule fois à l'ouverture
  useEffect(() => {
    api.get("/invoice-templates").then((r) => {
      const items = r.data.items || r.data.templates || [];
      setRecurringCount(items.filter((it) => it.status === "active" || it.active).length);
    }).catch(() => {});
    api.get("/import-jobs", { params: { limit: 20 } }).then((r) => {
      const jobs = r.data.jobs || [];
      setRunningJobs(jobs.filter((j) => j.status === "processing").length);
    }).catch(() => {});
  }, []);

  const setTab = (t) => {
    const p = new URLSearchParams(params);
    if (t === "all") p.delete("tab");
    else p.set("tab", t);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="invoices-hub">
      {/* Header commun */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Factures
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Toutes tes factures émises, récurrentes et imports IA — en un seul endroit.
        </p>
      </div>

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 h-auto">
          <TabsTrigger value="all" data-testid="invoices-tab-all"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <FileText className="h-4 w-4" />
            Toutes
          </TabsTrigger>
          <TabsTrigger value="recurring" data-testid="invoices-tab-recurring"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Repeat className="h-4 w-4" />
            Récurrentes
            {recurringCount !== null && recurringCount > 0 && (
              <Badge className="ml-1 bg-blue-100 text-blue-700 border border-blue-200 h-5 px-1.5 text-[10px]">
                {recurringCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="invoices-tab-import"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Upload className="h-4 w-4" />
            Import & OCR
            {runningJobs > 0 && (
              <Badge className="ml-1 bg-amber-100 text-amber-700 border border-amber-200 h-5 px-1.5 text-[10px] animate-pulse">
                {runningJobs}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <InvoicesList embedded />
        </TabsContent>
        <TabsContent value="recurring" className="mt-6">
          <RecurringInvoices embedded />
        </TabsContent>
        <TabsContent value="import" className="mt-6 space-y-6">
          <ImportVentesBatch embedded />
          <div className="pt-4 border-t border-slate-200">
            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Historique des imports
              </h2>
              <p className="text-xs text-slate-500">
                Les 20 derniers jobs d'import IA (ventes + achats).
              </p>
            </div>
            <ImportJobs embedded />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
